import { useEffect, useRef } from "react";
import "./SlashAnimation.css";

export default function SlashAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container  = defenderEl.closest(".battle-container");
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for defender
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate center of defender sprite
    const frameWidth = 192;  // Width of each frame (768px total ÷ 4 frames)
    const frameHeight = 192; // Height of the sprite
    const centerX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const centerY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Initial attacker animation (slight scale before attack)
    attackerEl.style.transition = 'transform 0.2s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    
    // Create and position animation element
    const slash = document.createElement("div");
    slash.className = "slash-anim";
    slash.style.setProperty('--sx', `${centerX - frameWidth / 2}px`);
    slash.style.setProperty('--sy', `${centerY - frameHeight / 2}px`);
    container.appendChild(slash);
    animRef.current = slash;
    
    // Create sparks at impact moment (frame 2)
    const sparkTimer = setTimeout(() => {
      // Flash effect
      const flash = document.createElement('div');
      flash.className = 'slash-flash';
      flash.style.left = `${centerX}px`;
      flash.style.top = `${centerY}px`;
      container.appendChild(flash);
      
      // Make defender react
      defenderEl.classList.add('slash-hit');
      
      // Remove flash and reset defender
      setTimeout(() => {
        flash.remove();
        defenderEl.classList.remove('slash-hit');
      }, 300);
      
      // Create small slash particles
      for (let i = 0; i < 8; i++) {
        const particle = document.createElement('div');
        particle.className = 'slash-particle';
        
        // Position around the impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 25;
        particle.style.left = `${centerX + Math.cos(angle) * distance}px`;
        particle.style.top = `${centerY + Math.sin(angle) * distance}px`;
        
        // Random rotation
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        
        container.appendChild(particle);
        
        // Auto-remove particles
        setTimeout(() => particle.remove(), 500 + Math.random() * 200);
      }
    }, 200); // Sync with frame 2 impact

    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.2s ease-out';
      attackerEl.style.transform = '';
    }, 400);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      if (slash.parentNode) {
        slash.remove();
      }
      onComplete();
    }, 800);

    // Cleanup function
    return () => {
      clearTimeout(sparkTimer);
      clearTimeout(resetTimer);
      clearTimeout(completeTimer);
      
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('slash-hit');
      }
      
      if (slash.parentNode) {
        slash.remove();
      }
      
      // Remove any flash or particles
      const elements = container.querySelectorAll('.slash-flash, .slash-particle');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SlashAnimation.moveType = "normal";
